import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { DATE_FORMAT, EVENT_TYPE, CORE_STATUSES, FILE_DOWNLOAD_API_PATH, SortColumnsType } from '@Constants/constants';
import * as types from '@Constants/actionTypes';
import Moment from 'moment';
import { Paginator } from 'primereact/paginator';
import { getDataTableSortFields, getSearchParamSortFields } from '@Helpers/sortHelper';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import MultiSelectBox from '@Common/MultiSelectBox/MultiSelectBox';
import Notification from '@Common/Notification';
import BulkUpdateResponsibleUserDrawer from '../../BulkUpdateResponsibleUserDrawer';
import Button from '@Common/Button';
import Modal from '@Common/Modal/Modal';
import Icon from '@Common/Icon';
import Tag from '@Common/Tag/Tag';
import DateRangePickerWrapper from '@Common/DateRangePicker/DateRangePickerWrapper';
import { getTaskStatusClassName } from '@Helpers/statusColors';
import { TruncateText } from '@Helpers/strings';
import { daysToDaysWeeks } from '@Helpers/dateTime';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { NavigateToRouteWithDashboardParams } from '@Helpers/routing';
import { REPORT_IDS } from '@Constants/constants';
import { Routes } from '@Constants/routes';
import { CUSTOMER_ROLES } from '@Constants/roles';
import { getMultiSelectFilterValues } from '@Helpers/multiselect';
import './ListView.scss';

export class ListView extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			cols: [],
			selectedTask: null,
			isUpdateResponsibleUserDrawerOpen: false,
			showDateRangePicker: false,
			selectedBulkValue: '',
			dateRange: {
				start: null,
				end: null,
				name: '',
			},
			checkedTasks: 0,
			dropFilterChanged: false,
		};
	}

	componentDidMount() {
		document.title = 'Task List';
		this.buildColumnOptions();
	}

	/**
	 * Jump through hoops to make sure we update the list of column options
	 * after we've finished loading the feature flags from the API.  By that
	 * time this component is already mounted and rendered.
	 */
	componentDidUpdate(prevProps) {
		const { isStartDateActive, isTaskDependenciesActive, isTaskStepsActive, isProjectWidgetActive, savedColumns, savedColumnOrder } = this.props.filters;

		const anyFeaturesChanged =
			isStartDateActive != prevProps.filters.isStartDateActive ||
			isTaskDependenciesActive != prevProps.filters.isTaskDependenciesActive ||
			isTaskStepsActive != prevProps.filters.isTaskStepsActive ||
			isProjectWidgetActive != prevProps.filters.isProjectWidgetActive;

		const savedColumnsChanged = savedColumns != prevProps.filters.savedColumns;
		const savedColumnOrderChanged = savedColumnOrder != prevProps.filters.savedColumnOrder;
		const selectedLocationsChanged = prevProps.searchParams.locationKeys != this.props.searchParams.locationKeys;

		if (anyFeaturesChanged || savedColumnsChanged || savedColumnOrderChanged || selectedLocationsChanged) {
			this.buildColumnOptions();
		}

		// IE/Edge hack
		// cross browser way of setting the background of the left most column of the datatable based on the status of the task
		const colorElements = document.getElementsByClassName('color-code');
		for (let i = 0; i < colorElements.length; i++) {
			let colorCodeClass;
			const div = colorElements[i].querySelector('div');
			if (div) {
				colorCodeClass = div.classList[0];
			}
			if (colorCodeClass) {
				colorElements[i].classList.add(colorCodeClass);
			}
		}
	}

	onSort = ({ sortField, sortOrder }) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		this.props.onSort(sortBy, sortDir);
	};

	onUpdateUserComplete = () => {
		this.props.updateSearchParams(null, null, true);
		this.closeUpdateUserDrawer();
	};

	closeUpdateUserDrawer = () => {
		this.setState({ isUpdateResponsibleUserDrawerOpen: false });
	};

	onDataTableRowClick = (e) => {
		const target = e.originalEvent.target;
		const { className = '', innerHTML = '', localName = '', classList } = target;
		const rowData = e.data;
		
		// Fix: Ensure className is always a string
		const classNameStr = typeof className === 'string' ? className : (className?.toString() || '');
		const isCheckboxElement = classNameStr.includes('p-checkbox') || innerHTML.includes('p-checkbox-box') || classList.contains('p-checkbox-icon');
		const isResponsibleUserLink = classNameStr.includes('text-primary');
		const isResponsibleUser = classNameStr.includes('responsible-user');
		const isChildElement = localName !== 'td';

		this.setState({ selectedTask: rowData });

		// Early returns for better performance
		if (isResponsibleUser || isChildElement) {
			return; // DO NOTHING if they're targeting the select box in the status column
		}
		
		if (isResponsibleUserLink) {
			this.setState({ isUpdateResponsibleUserDrawerOpen: true });
			return;
		}
		
		if (isCheckboxElement) {
			this.onToggleCheckedTask(rowData);
			return;
		}

		// Default action - show task
		this.props.showTask(rowData.id, rowData.baselineTaskId === 0 || rowData.baselineTaskId === null ? false : true);
	};

	milestoneTemplate = (rowData) => {
		return (
			<div className='milestone-row'>
				{rowData.isMilestone && (
					<div className='text-warning' title='Milestone Task'>
						<i className='fa-regular fa-diamonds-4'></i>
					</div>
				)}
				{rowData.links && rowData.links.length > 0 && (
					<div title='Has Links'>
						<Icon name='link' />
					</div>
				)}
				{rowData.hasFiles && (
					<div title='Has Files'>
						<i className='fa-regular fa-file'></i>
					</div>
				)}
				{rowData.lastComment && (
					<div title='Has Comments'>
						<i className='fa-regular fa-comment-dots'></i>
					</div>
				)}
				{rowData.isFollowedByCurrentUser && (
					<div title='Following task'>
						<Icon name='eye' />
					</div>
				)}
			</div>
		);
	};

	colorColumnTemplate = (rowData) => {
		const coreStatus = rowData.coreStatusId;
		const userDate = Moment.utc(rowData.userDate);
		const targetDate = Moment.utc(rowData.targetDate);
		const startDate = rowData.startDate ? Moment.utc(rowData.startDate) : null; // startDate can be null
		const classCode = getTaskStatusClassName(coreStatus, userDate, targetDate, startDate);

		return <div className={classCode} style={{ width: '5px' }}></div>;
	};

	onNameClick = (rowData) => {
		this.props.showTask(rowData.id, rowData.baselineTaskId === 0 || rowData.baselineTaskId === null ? false : true);
	};

	nameTemplate = (rowData) => {
		return (
			<span className='task-name show-newlines task-description-column' onClick={() => this.onNameClick(rowData)}>
				{rowData.name}
			</span>
		);
	};

	additionalInfoTemplate = (rowData) => {
		return <span className='show-newlines'>{TruncateText(rowData.additionalInfo, 200)}</span>;
	};

	prereqsTemplate = (rowData) => {
		if (rowData.prereqs && rowData.prereqs.length > 0) {
			const prereqs = rowData.prereqs.length;
			const completed = rowData.prereqs.filter((m) => m.coreStatusName === 'Complete').length;
			return completed + '/' + prereqs;
		} else {
			return 'N/A';
		}
	};

	stepTemplate = (rowData) => {
		if (rowData.steps && rowData.steps.length > 0) {
			const activeSteps = rowData.steps.filter((m) => m.isActive);
			const completed = activeSteps.filter((m) => m.isCompleted);
			const stepsCompleted = completed ? completed.length : 0;

			return `${stepsCompleted}/${activeSteps.length} done`;
		} else {
			return 'N/A';
		}
	};

	linkTemplate = (rowData) => {
		let retval = [];

		if (rowData.links && rowData.links.length > 0) {
			for (let i = 0; i < rowData.links.length; i++) {
				const link = rowData.links[i];

				// Check if URL contains download API endpoint to determine if it's a file
				const isFileDownload = link.url && link.url.includes(FILE_DOWNLOAD_API_PATH);

				// Create a unique key using both the link ordinal and array index
				const uniqueKey = `${rowData.id}-link-${link.ordinal || 'no-ordinal'}-${i}`;

				if (isFileDownload) {
					// This is a file - use download API call
					retval.push(
						<button key={uniqueKey} type='button' className='btn btn-link' onClick={(e) => this.onDownloadLink(link, e)}>
							{link.name}
						</button>
					);
				} else if (link.url) {
					// This is an external URL - open in new tab
					// Ensure the URL has a protocol, otherwise browser treats it as relative
					const fullUrl = link.url.startsWith('http://') || link.url.startsWith('https://') 
						? link.url 
						: `https://${link.url}`;
					
					retval.push(
						<div key={uniqueKey}>
							<a href={fullUrl} rel='noopener noreferrer' target='_blank'>
								{link.name}
							</a>
						</div>
					);
				} else {
					// No URL available
					retval.push(
						<span key={uniqueKey}>
							{link.name}
						</span>
					);
				}
			}
		}

		return retval;
	};

	onDownloadLink = async (link, e) => {
		e.preventDefault();
		try {
			// extract the file name from the link.url
			let start = link.url.indexOf('=') + 1;
			let fileName = link.url.slice(start);

			await this.props.filesActions.download(fileName);
			Notification('success', 'Download Started', 'File download has been initiated.');
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	contactInfoTemplate = (rowData) => {
		return (
			<div className='contact-info'>
				{rowData.contactName ? <div>{rowData.contactName}</div> : ''}
				{rowData.contactEmail ? <div>{rowData.contactEmail}</div> : ''}
			</div>
		);
	};

	responsibleUserTemplate = (rowData) => {
		let name = '';
		const assignToMe =
			rowData.responsibleUserId != this.props.userId ? (
				<div>
					<span className='responsible-user-assign' onClick={() => this.onAssignToMe(rowData.id)}>
						Assign Task to Me
					</span>
				</div>
			) : (
				''
			);
		if (rowData.responsibleUserId) {
			name = (
				<div>
					<div>{rowData.responsibleUserName}</div>
					<div className='responsible-user-jobtitle'>{rowData.responsibleUserJobTitle}</div>
				</div>
			);
		} else {
			name = <div>{rowData.jobTitleName}</div>;
		}

		return (
			<div className='responsible-user'>
				{name}
				{assignToMe}
			</div>
		);
	};

	baselineTemplate = (rowData) => {
		return rowData.baselineName ? rowData.baselineName : '';
	};

	phaseTemplate = (rowData) => {
		return rowData.phaseName ? rowData.phaseName : '';
	};

	communityTemplate = (rowData) => {
		return rowData.communityName ? rowData.communityName : '';
	};

	locationTemplate = (rowData) => {
		return rowData.locationName ? rowData.locationName : '';
	};

	projectTemplate = (rowData) => {
		const hasAnyRowData =
			rowData.isMilestone || rowData.links?.length > 0 || rowData.hasFiles || rowData.lastComment || rowData.isFollowedByCurrentUser;

		return (
			<div className='project-row'>
				{hasAnyRowData && <span className='ms-2'>{this.milestoneTemplate(rowData)}</span>}
				<span className={`${!hasAnyRowData ? 'project-name' : ''}`}>{rowData.projectName || ''}</span>
			</div>
		);
	};

	departmentTemplate = (rowData) => {
		return rowData.departmentName ? rowData.departmentName : '';
	};

	modifiedDateTemplate = (rowData) => {
		return rowData.lastModifiedDate ? Moment.utc(rowData.lastModifiedDate).format(DATE_FORMAT) : '';
	};

	createdDateTemplate = (rowData) => {
		return Moment.utc(rowData.createdDate).format(DATE_FORMAT);
	};

	startDateTemplate = (rowData) => {
		if (rowData.startDate && rowData.startDate.trim().length > 0) {
			return Moment.utc(rowData.startDate).format(DATE_FORMAT);
		} else {
			return '';
		}
	};

	statusTemplate = (rowData) => {
		const statusId = rowData.statusId ? rowData.statusId.toString() : '';
		return (
			<div className='select-column-header'>
				<MultiSelectBox
					name='statusId'
					// label='Status'
					className='mb-0'
					placeholder='Status'
					idField='id'
					valueField='name'
					options={this.props.columnFilters.statuses}
					selectedValues={statusId}
					onChange={this.onChangeStatus}
					isMulti={false}
				/>
			</div>
		);
	};

	onAssignToMe = async (taskId) => {
		// updates the task when they click the Assign to Me in the Responsible User column
		try {
			const response = await this.props.locationTaskActions.updateResponsibleUser([taskId], this.props.userId);
			if (response.type === types.UPDATE_LOCATION_TASK_LIST_RESPONSIBLE_USER_SUCCESS) {
				Notification('success', 'Responsible User Updated', '');
				this.props.onAssignToMeComplete();
			} else if (response.type === types.UPDATE_LOCATION_TASK_LIST_RESPONSIBLE_USER_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onChangeStatus = async (e) => {
		const taskId = this.state.selectedTask.id;
		const statusId = e;

		try {
			const response = await this.props.locationTaskActions.updateStatus([taskId], statusId);
			if (response.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_SUCCESS) {
				// If status was set to core complete, show celebration confetti
				const completeStatus = this.props.columnFilters.statuses.find(({ coreStatusId }) => coreStatusId === CORE_STATUSES.Complete);
				if (statusId === completeStatus.id) {
					const event = new Event(EVENT_TYPE.ConfettiCannon);
					document.dispatchEvent(event);
				}

				Notification('success', 'Status Updated', 'New Status has been set');
				this.props.onStatusChangedComplete();
			} else if (response.type === types.UPDATE_LOCATION_TASK_LIST_STATUS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	targetDateTemplate = (rowData) => {
		const targetDate = Moment.utc(rowData.targetDate);
		const targetDateFormatted = targetDate.format(DATE_FORMAT);

		if (rowData.statusName === 'Complete' || rowData.statusName === 'Not Applicable') {
			return <div className='text-success'>{targetDateFormatted}</div>;
		} else {
			if (Moment().isAfter(targetDate.add(1, 'days'))) {
				return <div className='text-danger'>{targetDateFormatted}</div>;
			} else {
				return targetDateFormatted;
			}
		}
	};

	checkboxTemplate = (rowData) => {
		return (
			<Checkbox 
				name='checkbox' 
				onChange={() => this.onToggleCheckedTask(rowData)}
				checked={rowData.isChecked}
			/>
		);
	};

	lastCommentTemplate = (rowData) => {
		if (rowData.lastComment) {
			const comment = TruncateText(rowData.lastComment.comment, 200);
			return (
				<div>
					<span className='comment-rendered text-break' dangerouslySetInnerHTML={{ __html: comment }}></span>
					<div className='text-muted'>
						{rowData.lastComment.createdBy} @ {Moment.utc(rowData.lastComment.createdDate).format(DATE_FORMAT)}
					</div>
				</div>
			);
		} else {
			return '';
		}
	};

	tagsTemplate = (rowData) => {
		const retval = [];

		if (rowData.tags && rowData.tags.length > 0) {
			for (let i = 0; i < rowData.tags.length; i++) {
				const tag = rowData.tags[i];

				// Create a unique key using rowData.id, tag name, and index
				const uniqueKey = `${rowData.id}-tag-${tag.name}-${i}`;

				retval.push(
					<div className='mb-1' key={uniqueKey}>
						<Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
					</div>
				);
			}
		}
		return retval;
	};

	onToggleCheckedTask = (task) => {
		var checkedTasks = this.props.onToggleCheckedTask(task.id);

		this.setState({ checkedTasks });
	};

	onMasterCheckboxToggle = () => {
		var checkedTasks = this.props.onMasterCheckboxToggle();

		this.setState({ checkedTasks });
	};

	isColumnFeatureEnabled = (fieldName) => {
		// determines if the columns should be shown based on special rules
		switch (fieldName) {
			case 'projectName':
				return this.props.filters.isProjectWidgetActive;
			case 'startDate':
				return this.props.filters.isStartDateActive;
			case 'steps':
				return this.props.filters.isTaskStepsActive;
			case 'prereqs':
				return this.props.filters.isTaskDependenciesActive;
			case 'locationName':
				/**
				 * 1320: Only show the locationName column when more than one
				 * location has been selected in the header's location selector.
				 */
				return this.props.searchParams.locationKeys && this.props.searchParams.locationKeys.length > 1;
			default:
				return true;
		}
	};

	setColumnTemplate = (column) => {
		switch (column.field) {
			case 'name':
				column.body = this.nameTemplate;
				break;
			case 'additionalInfo':
				column.body = this.additionalInfoTemplate;
				break;
			case 'projectName':
				column.body = this.projectTemplate;
				// 1512: Shrink Project to make more room for Task
				column.style = { width: '140px' };
				break;
			case 'startDate':
				column.body = this.startDateTemplate;
				column.style = { width: '95px' };
				break;
			case 'targetDate':
				column.body = this.targetDateTemplate;
				column.style = { width: '105px' };
				break;
			case 'responsibleUserName':
				column.body = this.responsibleUserTemplate;
				column.style = { width: '140px' }; // Explicitly set Responsible User column width so header doesn't wrap when bulk mode enabled
				break;
			case 'departmentName':
				column.body = this.departmentTemplate;
				// 1512: Shrink Dept to make more room for Task
				column.style = { width: '140px' };
				break;
			case 'links':
				column.body = this.linkTemplate;
				break;
			case 'steps':
				column.body = this.stepTemplate;
				column.style = { width: '76px' };
				break;
			case 'prereqs':
				column.body = this.prereqsTemplate;
				break;
			case 'locationName':
				column.body = this.locationTemplate;
				break;
			case 'createdDate':
				column.body = this.createdDateTemplate;
				column.style = { width: '108px' };
				break;
			case 'lastModifiedDate':
				column.body = this.modifiedDateTemplate;
				column.style = { width: '112px' };
				break;
			case 'contactInfo':
				column.body = this.contactInfoTemplate;
				break;
			case 'communityName':
				column.body = this.communityTemplate;
				break;
			case 'phaseName':
				column.body = this.phaseTemplate;
				break;
			case 'baselineName':
				column.body = this.baselineTemplate;
				break;
			case 'statusName':
				column.body = this.statusTemplate;
				column.style = { width: '150px' };
				break;
			case 'id':
				column.style = { width: '76px' };
				break;
			case 'baselineTaskId':
				column.style = { width: '76px' };
				break;
			case 'targetWeeksDays':
				column.body = (rowData) => {
					return daysToDaysWeeks(rowData.targetDaysOut);
				};
				column.style = { width: '76px' };
				break;
			case 'discipline':
				column.style = { width: '76px' };
				break;
			case 'lastComment':
				column.body = this.lastCommentTemplate;
				column.style = { maxWidth: '25%' };
				break;
			case 'tags':
				column.body = this.tagsTemplate;
				break;
		}
	};

	buildColumnOptions = () => {
		// Define system columns once to avoid repeated allocations
		const systemColumns = ['coreStatusId', 'isChecked'];
		
		// Helper function to build column objects consistently
		const buildColumn = (savedColumn, masterColumn) => ({
			field: savedColumn.fieldName,
			header: savedColumn.displayName,
			isSortable: masterColumn.isSortable,
		});
		
		let columns = [];

		// Check if we have saved columns from the backend
		if (this.props.filters.savedColumns && this.props.filters.savedColumns.length > 0) {
			// Filter out system columns from saved columns immediately to ensure consistent behavior
			let remainingColumns = this.props.filters.savedColumns.filter(
				col => !systemColumns.includes(col.fieldName)
			);
			
			// Start with an empty array to build the columns in the correct order
			let orderedColumns = [];
			
			// If we have a saved column order, use it to determine the sequence
			if (this.props.filters.savedColumnOrder && this.props.filters.savedColumnOrder.length > 0) {
				// Use a Set to track processed field names for O(1) lookup performance
				const processedFieldNames = new Set();
				
				// First, add columns in the order specified by savedColumnOrder
				for (let i = 0; i < this.props.filters.savedColumnOrder.length; i++) {
					const fieldName = this.props.filters.savedColumnOrder[i];
					
					// Skip system columns - they're handled separately in JSX
					if (systemColumns.includes(fieldName)) {
						continue;
					}
					
					// Find this column in savedColumns
					const savedColumn = this.props.filters.savedColumns.find((col) => col.fieldName === fieldName);
					if (savedColumn) {
						// Find the master column for additional configuration
						const masterColumn = this.props.filters.masterColumns.find((col) => col.fieldName === fieldName);
						
						if (masterColumn) {
							let column = buildColumn(savedColumn, masterColumn);
							this.setColumnTemplate(column);
							
							// Track this column as processed regardless of feature enablement
							// to prevent reprocessing in the remaining columns loop
							processedFieldNames.add(fieldName);

							if (this.isColumnFeatureEnabled(column.field)) {
								orderedColumns.push(column);
							}
						}
					}
				}
				
				// Filter remaining columns once after processing ordered columns
				remainingColumns = remainingColumns.filter(col => !processedFieldNames.has(col.fieldName));
			}
			
			// Add any remaining columns that weren't in savedColumnOrder (newly selected columns)
			for (let i = 0; i < remainingColumns.length; i++) {
				const savedColumn = remainingColumns[i];
				
				// Find the corresponding master column for configuration
				const masterColumn = this.props.filters.masterColumns.find((col) => col.fieldName === savedColumn.fieldName);

				if (masterColumn) {
					let column = buildColumn(savedColumn, masterColumn);
					this.setColumnTemplate(column);

					if (this.isColumnFeatureEnabled(column.field)) {
						orderedColumns.push(column);
					}
				}
			}
			
			columns = orderedColumns;
		} else {
			// Fallback to default columns when no saved columns exist
			for (let i = 0; i < this.props.filters.masterColumns.length; i++) {
				const masterColumn = this.props.filters.masterColumns[i];

				let column = {
					field: masterColumn.fieldName,
					header: masterColumn.displayName,
					isSortable: masterColumn.isSortable,
				};

				this.setColumnTemplate(column);
				if (masterColumn.isDefault && this.isColumnFeatureEnabled(column.field)) {
					columns.push(column);
				}
			}
		}

		this.setState({
			cols: columns,
		});
	};

	onTextFilterChange = (e) => {
		this.props.updateSearchParams(e.target.name, e.target.value, false);
	};

	onSearchBlur = () => {
		this.props.updateSearchParams(null, null, true);
	};

	keyUpFilter = (e) => {
		if (e.keyCode == 13) {
			this.props.updateSearchParams(null, null, true);
		}
	};

	onPageChange = (e) => {
		this.props.updateSearchParams('pageNumber', e.page + 1, true);
	};

	onTriggerSearchFromCustomComponent = (clicked) => {
		if (clicked) {
			this.props.updateSearchParams(null, null, true);
		}
	};

	onBulkMultiSelectChange = (value) => {
		if (['FollowTask', 'UnfollowTask', 'Inactive', 'ShiftDates'].includes(value) && this.state.checkedTasks === 0) {
			Notification('error', 'No tasks selected', 'Please select at least one task to apply this action.');
			return;
		}

		this.setState({ selectedBulkValue: value }, () => {
			switch (value) {
				case 'ChangeStatus':
					this.props.bulkUpdateStatus?.();
					this.resetBulkMultiSelectValue();
					break;
				case 'ChangeResponsible':
					this.props.bulkUpdateResponsibleUser?.();
					this.resetBulkMultiSelectValue();
					break;
				default:
					break;
			}
		});
	};

	resetBulkMultiSelectValue = () => {
		this.setState({ checkedTasks: 0, selectedBulkValue: '' });
	};

	// remember which date range we are working with and show the modal
	// also populate any existing searchParams values for that particular range
	showDateRangePicker = (rangeName) => {
		let start = null;
		let end = null;

		switch (rangeName) {
			case 'startDate':
				start = this.props.searchParams.startDateStart ? this.props.searchParams.startDateStart : null;
				end = this.props.searchParams.startDateEnd ? this.props.searchParams.startDateEnd : null;
				break;
			case 'targetDate':
				start = this.props.searchParams.targetDateStart ? this.props.searchParams.targetDateStart : null;
				end = this.props.searchParams.targetDateEnd ? this.props.searchParams.targetDateEnd : null;
				break;
		}

		if (typeof end === 'string') {
			end = Moment.utc(Date.parse(end));
		}
		if (typeof start === 'string') {
			start = Moment.utc(Date.parse(start));
		}

		const newDateRange = {
			...this.state.dateRange,
			name: rangeName,
			start: start,
			end: end,
		};
		this.setState({
			showDateRangePicker: true,
			dateRange: newDateRange,
		});
	};

	// the event that fires when a new date in the range picker is selected
	// remember these so we can add to search params if they hit the Apply button on the modal
	onStartRangeChange = (startDate, endDate) => {
		const newDateRange = {
			...this.state.dateRange,
			start: startDate,
			end: endDate,
		};
		this.setState({ dateRange: newDateRange });
	};

	// the event that fires when the Apply button is clicked in a date range modal
	onSelectDateRange = async () => {
		this.setState({ showDateRangePicker: false });

		// Convert JS Date => Moment => format
		const startAsMoment = this.state.dateRange.start ? Moment(this.state.dateRange.start) : null;

		const endAsMoment = this.state.dateRange.end ? Moment(this.state.dateRange.end) : null;

		if (this.state.dateRange.name === 'startDate') {
			let newParams = {
				...this.props.searchParams,
				startDateStart: startAsMoment ? startAsMoment.format(DATE_FORMAT) : null,
				startDateEnd: endAsMoment ? endAsMoment.format(DATE_FORMAT) : null,
			};
			await this.props.locationTaskActions.setLocationFilterState(newParams);
		} else if (this.state.dateRange.name === 'targetDate') {
			let newParams = {
				...this.props.searchParams,
				targetDateStart: startAsMoment ? startAsMoment.format(DATE_FORMAT) : null,
				targetDateEnd: endAsMoment ? endAsMoment.format(DATE_FORMAT) : null,
			};
			await this.props.locationTaskActions.setLocationFilterState(newParams);
		}

		this.props.updateSearchParams(null, null, true);
	};

	onCancelDateRange = () => {
		this.setState({ showDateRangePicker: false });
	};

	getColumnFilterMultiSelectValues = (sourceIds, columnFiltersValues) => {
		let results = [];

		if (!sourceIds || sourceIds.length === 0) return [];

		if (columnFiltersValues && columnFiltersValues.length > 0) {
			for (let i = 0; i < sourceIds.length; i++) {
				const value = columnFiltersValues.find((m) => m.id.toString() === sourceIds[i].toString());
				if (value) {
					results.push(value);
				}
			}
		}

		return results;
	};

	applyBulkAction = () => {
		switch (this.state.selectedBulkValue) {
			case 'Inactive':
				this.props.bulkUpdateInactive();
				this.resetBulkMultiSelectValue();
				break;
			case 'FollowTask':
			case 'UnfollowTask':
				this.props.bulkUpdateFollowTask(this.state.selectedBulkValue);
				this.resetBulkMultiSelectValue();
				break;
			case 'ShiftDates':
				this.props.bulkShiftDates(this.state.selectedBulkValue);
				this.resetBulkMultiSelectValue();
				break;
			default:
				return;
		}
	};

	handleMultiSelectChange = (key) => (e) => {
		this.props.updateSearchParams(key, getMultiSelectFilterValues(e.value), false);
		this.setState({ dropFilterChanged: true });
	};

	handleMultiSelectClose = () => {
		if (this.state.dropFilterChanged === true) {
			this.props.updateSearchParams(null, null, true);
			this.setState({ dropFilterChanged: false });
		}
	};

	customCloseIcon = () => {
		return (
			<i
				className={classNames('pi', {
					'pi-times': !this.state?.dropFilterChanged,
					'pi-check-square text-success': this.state?.dropFilterChanged,
				})}
			/>
		);
	};

	renderColumns = () => {
		// because the multiselect needs an object and not just an id
		const departmentValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.departmentIds, this.props.columnFilters.departments);
		const projectValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.projectIds, this.props.columnFilters.projects);
		const communityValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.communityIds, this.props.columnFilters.communities);
		const phaseValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.phaseIds, this.props.columnFilters.phases);
		const statusValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.statusIds, this.props.columnFilters.statuses);
		const tagValues = this.getColumnFilterMultiSelectValues(this.props.searchParams.tagIds, this.props.columnFilters.tags);

		const filters = {
			name: (
				<input
					type='text'
					name='name'
					className='p-inputtext'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.name}
					placeholder='Search'
				/>
			),
			projectName: (
				<MultiSelect
					value={projectValues}
					onChange={this.handleMultiSelectChange('projectIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.projects}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			departmentName: (
				<MultiSelect
					value={departmentValues}
					onChange={this.handleMultiSelectChange('departmentIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.departments}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			communityName: (
				<MultiSelect
					value={communityValues}
					onChange={this.handleMultiSelectChange('communityIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.communities}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			phaseName: (
				<MultiSelect
					value={phaseValues}
					onChange={this.handleMultiSelectChange('phaseIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.phases}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			responsibleUserName: (
				<input
					type='text'
					name='responsibleUserName'
					className='p-inputtext'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.responsibleUserName}
					placeholder='Search'
				/>
			),
			startDate: (
				<Button
					type='button'
					bootstrapClass='light'
					name='startDateRangeButton'
					text={this.props.searchParams.startDateStart || this.props.searchParams.startDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => this.showDateRangePicker('startDate')}
				/>
			),
			targetDate: (
				<Button
					type='button'
					bootstrapClass='light'
					name='targetDateRangeButton'
					text={this.props.searchParams.targetDateStart || this.props.searchParams.targetDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => this.showDateRangePicker('targetDate')}
				/>
			),
			additionalInfo: (
				<input
					type='text'
					name='additionalInfo'
					className='p-inputtext'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.additionalInfo}
					placeholder='Search'
				/>
			),
			baselineName: (
				<input
					type='text'
					name='baselineName'
					className='p-inputtext'
					onBlur={this.onSearchBlur}
					onChange={this.onTextFilterChange}
					onKeyUp={this.keyUpFilter}
					value={this.props.searchParams.baselineName}
					placeholder='Search'
				/>
			),
			statusName: (
				<MultiSelect
					value={statusValues.length > 0 ? statusValues : null}
					onChange={this.handleMultiSelectChange('statusIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.statuses}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			tags: (
				<MultiSelect
					value={tagValues}
					onChange={this.handleMultiSelectChange('tagIds')}
					onHide={this.handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={this.props.columnFilters.tags}
					placeholder='Filter'
					closeIcon={this.customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
		};

		return this.state.cols.map((col) => {
			const filterElement = filters[col.field];
			return filterElement ? (
				<Column
					key={col.field}
					sortable={col.isSortable}
					field={col.field}
					filter
					filterElement={filterElement}
					header={col.header}
					body={col.body}
					style={col.style}
				/>
			) : (
				<Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} style={col.style} />
			);
		});
	};

	onExport = () => {
		const {
			locationKeys,
			statusIds,
			departmentIds,
			isMilestone,
			phaseIds,
			disciplineIds,
			communityIds,
			targetDateStart,
			targetDateEnd,
			projectIds,
			tagIds,
			isFollowedByCurrentUser,
		} = this.props.searchParams;

		const urlParams = {
			LocationKeys: locationKeys,
			StatusId: statusIds,
			DepartmentId: departmentIds,
			IsMilestone: isMilestone,
			DisciplineId: disciplineIds,
			PhaseId: phaseIds,
			CommunityId: communityIds,
			TargetDateStart: targetDateStart ? [Moment(targetDateStart).format(DATE_FORMAT).toString()] : null,
			TargetDateEnd: targetDateEnd ? [Moment(targetDateEnd).format(DATE_FORMAT).toString()] : null,
			ProjectId: projectIds,
			TagId: tagIds,
			IsFollowedByCurrentUser: isFollowedByCurrentUser,
		};

		NavigateToRouteWithDashboardParams(Routes.Reports + '/' + REPORT_IDS.TaskList, urlParams, false);
	};

	onColumnReorder = async (e) => {
		try {			
			// Extract field names from reordered columns, filtering out null/undefined values
			const newColumnOrder = e.columns
				.map((column) => column.props.field)
				.filter((field) => field != null);
			
			// Update local component state with the new column order
			this.updateLocalColumnState(newColumnOrder);
			
			// Save the new column order to the backend
			await this.saveColumnOrderToBackend(newColumnOrder);
			
		} catch (error) {
			console.error('Error during column reorder:', error);
			Notification('error', 'Error', 'Failed to save column order. Please try again.');
		}
	};

	updateLocalColumnState = (newColumnOrder) => {
		const reorderedCols = newColumnOrder
			.map((fieldName) => this.state.cols.find((col) => col.field === fieldName))
			.filter((col) => col != null);
		
		this.setState({ cols: reorderedCols });
	};

	saveColumnOrderToBackend = async (newColumnOrder) => {
		const request = {
			columns: newColumnOrder,
			columnsType: SortColumnsType.TaskListPage
		};
		
		const response = await this.props.userActions.saveUsersColumns(request);
		
		if (response.type === types.SAVE_USER_COLUMNS_SUCCESS) {
			Notification('success', 'Success', 'Your column order has been saved.');
		} else if (response.type === types.SAVE_USER_COLUMNS_FAILURE) {
			Notification('error', 'Error', response.payload.message);
		}
	};

	render() {
		const masterCheckbox = (
			<div>
				<Checkbox name='checkbox' onChange={() => this.onMasterCheckboxToggle()} checked={this.props.allTasksChecked}></Checkbox>
			</div>
		);

		const { sortField, sortOrder } = getDataTableSortFields(this.props.searchParams);

		const bulkOptions = [
			{ label: 'Change Status', value: 'ChangeStatus' },
			{ label: 'Change Responsible', value: 'ChangeResponsible' },
			{ label: 'Follow Task', value: 'FollowTask' },
			{ label: 'Unfollow Task', value: 'UnfollowTask' },
			{ label: 'Shift Dates', value: 'ShiftDates' },
			{ label: 'Inactive', value: 'Inactive' },
		];

		const { userRole, filters } = this.props;

		const shouldShowInactive = [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Owner.name].includes(userRole);

		let filteredOptions = bulkOptions.filter((o) => {
			if (o.value === 'Inactive' && !shouldShowInactive) return false;
			if (o.value === 'ShiftDates' && !filters.allowLocationTaskDateChanges) return false;
			return true;
		});

		const validBulkActions = ['FollowTask', 'UnfollowTask', 'Inactive', 'ShiftDates'];

		const convertedOptions = filteredOptions.map((option) => {
			const label = typeof option === 'string' ? option : option.label;
			return {
				label,
				value: label.replace(/\s/g, ''),
			};
		});

		const { checkedTasks, selectedBulkValue } = this.state;
		const shouldShowApplyButton = checkedTasks > 0 && validBulkActions.includes(selectedBulkValue);

		const header = (
			<div className='table-header-container'>
				<div className='row'>
					<div className='col-md-6'>
						<div className='btn-group'>
							<MultiSelectBox
								className='pacer-multi-select bulk-multi-select'
								name='bulkActions'
								options={convertedOptions}
								onChange={this.onBulkMultiSelectChange}
								idField='value'
								valueField='label'
								placeholder='Bulk Actions'
								isMulti={false}
								selectedValues={this.state.selectedBulkValue}
							/>
							{shouldShowApplyButton && (
								<button type='button' className='btn pacer61-purple-btn ms-3' onClick={this.applyBulkAction}>
									Apply
								</button>
							)}
						</div>
					</div>
					<div className='col-md-6'>
						<div className='d-flex justify-content-end'>
							<button
								type='button'
								className='btn pacer61-secondary-btn me-2'
								onClick={this.props.bulkUpdateByJobTitle}
								alt='Assign tasks by Job Title'
								title='Assign tasks by Job Title'>
								<i className='fa-light fa-split fa-xl'></i>
							</button>
							<button
								type='button'
								className='btn pacer61-secondary-btn me-2'
								onClick={this.onExport}
								alt='Export To PDF'
								title='Export To PDF'>
								<i className='fa-regular fa-cloud-arrow-down fa-xl'></i>
							</button>
							<button
								type='button'
								className='btn pacer61-secondary-btn me-2'
								onClick={this.props.onColumnsClick}
								alt='Choose Columns'
								title='Choose Columns'>
								<i className='fa-regular fa-columns-3 fa-xl'></i>
							</button>
						</div>
					</div>
				</div>
			</div>
		);

		const noTasksFound = (
			<div className='d-flex justify-content-center align-items-center'>
				<p className='fs-5'>No tasks found. Click <a style={{ cursor: 'pointer' }} className='link-primary' onClick={this.props.clearAllFilters}>here</a> to clear all filters.</p>
			</div>
		);

		return (
			<div className='list-view'>
				{this.props.searchParams.locationKeys.length > 0 && (
					<LoadingIndicator isLoading={this.props.isLoading} isFullscreen={true} hideWhileLoading={true}>
						<Fragment>
							<DataTable
								filterDisplay='row'
								header={header}
								className='p-datatable-sm p-datatable-striped'
								value={this.props.locationTasks}
								onRowClick={this.onDataTableRowClick}
								sortField={sortField}
								sortOrder={sortOrder}
								onSort={this.onSort}
								reorderableColumns={true}
								onColReorder={this.onColumnReorder}
								emptyMessage={noTasksFound}>
								<Column
									className='color-code'
									field='coreStatusId'
									body={this.colorColumnTemplate}
									style={{ width: '5px', padding: '0px' }}
								/>
								<Column
									field='isChecked'
									filter
									filterElement={masterCheckbox}
									body={this.checkboxTemplate}
									style={{ textAlign: 'center', width: '40px' }}
								/>
								{this.renderColumns()}
							</DataTable>
							{this.props.totalCount > this.props.searchParams.pageSize && (
								<Paginator
									rows={this.props.searchParams.pageSize}
									totalRecords={this.props.totalCount}
									first={(this.props.searchParams.pageNumber - 1) * this.props.searchParams.pageSize}
									onPageChange={this.onPageChange}
								/>
							)}
							<div className='results-count'>Total records: {this.props.totalCount}</div>
						</Fragment>
					</LoadingIndicator>
				)}

				{this.state.isUpdateResponsibleUserDrawerOpen ? (
					<BulkUpdateResponsibleUserDrawer
						locationTaskActions={this.props.locationTaskActions}
						onComplete={this.onUpdateUserComplete}
						onCancel={this.closeUpdateUserDrawer}
						locationTasks={[this.state.selectedTask]}
						locationKeys={this.props.searchParams.locationKeys}
					/>
				) : null}
				{this.state.showDateRangePicker ? (
					<Modal
						id='startDateRangeModal'
						title='Select Date Range'
						body={
							<div className='datepicker-container'>
								<DateRangePickerWrapper
									onDatesChange={this.onStartRangeChange}
									initialStartDate={this.state.dateRange.start}
									initialEndDate={this.state.dateRange.end}
								/>
							</div>
						}
						primaryButtonText='Apply'
						onPrimaryClick={this.onSelectDateRange}
						onSecondaryClick={this.onCancelDateRange}
						onCloseClick={this.onCancelDateRange}
					/>
				) : null}
			</div>
		);
	}
}

ListView.propTypes = {
	locationTaskActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	userActions: PropTypes.object.isRequired,
	isLoading: PropTypes.bool.isRequired,
	locationTasks: PropTypes.array.isRequired,
	totalCount: PropTypes.number.isRequired,
	filters: PropTypes.object.isRequired,
	columnFilters: PropTypes.object.isRequired,
	searchParams: PropTypes.object.isRequired,
	allTasksChecked: PropTypes.bool.isRequired,
	onSort: PropTypes.func.isRequired,
	updateSearchParams: PropTypes.func.isRequired,
	clearAllFilters: PropTypes.func.isRequired,
	onStatusChangedComplete: PropTypes.func.isRequired,
	onMasterCheckboxToggle: PropTypes.func.isRequired,
	onToggleCheckedTask: PropTypes.func.isRequired,
	showTask: PropTypes.func.isRequired,
	bulkUpdateMode: PropTypes.bool.isRequired,
	userId: PropTypes.number.isRequired,
	onAssignToMeComplete: PropTypes.func.isRequired,
	bulkUpdateStatus: PropTypes.func.isRequired,
	bulkUpdateResponsibleUser: PropTypes.func.isRequired,
	bulkUpdateInactive: PropTypes.func.isRequired,
	bulkUpdateFollowTask: PropTypes.func.isRequired,
	bulkShiftDates: PropTypes.func.isRequired,
	bulkUpdateByJobTitle: PropTypes.func.isRequired,
	onColumnsClick: PropTypes.func.isRequired,
	userRole: PropTypes.string.isRequired,
};

export default memo(ListView);
